import React, { useState } from "react";
import { NXAlertCircle, NXDelete, NXPlus } from "../../icons";
import enumChoice, { getEnumItem } from "../../enum";
import { InputField } from "../../components/common";
import { ImageField } from "../images";
import CurrencyInputField from "../common/currencyInputField";

interface VariantSectionProps {
  productConfig: any;
  serial: number;
  colors: any[];
  deleteVariant: any;
  markAsDefault?: any;
  onChange: any;
  errorData?: any;
  name?: string;
  variant?: any;
}

const VariantSection: React.FC<VariantSectionProps> = ({
  productConfig,
  serial,
  colors,
  deleteVariant,
  markAsDefault,
  errorData,
  onChange,
  name,
  variant,
}) => {
  const [metaSection, setMetaSection] = useState<number>(0);
  const [variantData, setVariantData] = useState<any>({});

  // check if variant completely missing
  const isVariantMissingError = errorData?.variants_payload && Array.isArray(errorData?.variants_payload) && typeof errorData?.variants_payload[0] === 'string';

  const addNewMetasection = (event: any) => {
    event.preventDefault();
    setMetaSection(prevVariantSection => prevVariantSection + 1);
  };

  const onChangeHandler = (e: any) => {
    onChange({...variant, [e.target.name]: e.target.value}, serial - 1)
  };

  const onWeightValueChange = (e: any) => {
    onChange({...variant, [e.target.name]: e.target.value, 'weight_unit': productConfig.weight_unit}, serial - 1)
  }

  const onSingleChange = (name: string, value:any) => {
    onChange({...variant, [name]: value}, serial - 1)
  };

  const style = {
    control: (provided:any, state:any) => ({
      ...provided,
      padding: '6px',
      borderRadius: '10px',
    }),
  }

  const handleSingleChange = (name:any, data: any) => {
    if (data) {
      onChange({...variant, [name]: data}, serial - 1 );
    }
  }
  
  return (
    <div className={`p-6 border-[1px] border-solid rounded-xl relative transition-all duration-200 hover:shadow-md ${isVariantMissingError ? 'border-red-500 bg-red-50' : 'border-base-200'}`}>
      {/* Delete Button */}
      {productConfig.has_variant && (
        <button 
          onClick={(e) => deleteVariant(e, variant?.id || false, serial, variant?.is_default_variant)}  
          className={`absolute top-4 right-4 bg-red-500 hover:bg-red-600 px-2 py-2 rounded-lg transition-colors duration-200`}
        > 
          <NXDelete className="text-white" /> 
        </button>
      )}
      
      {/* Variant Header */}
      <div className="flex items-center gap-3 mb-6">
        <h1 className="font-nunito font-[900] text-2xl text-gray-800">
          {productConfig.has_variant ? `Variant - ${serial}` : `Info`}
        </h1>
        <NXAlertCircle className="text-primary-500" />

        {productConfig.has_variant && variant?.is_default_variant && 
          <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full ml-2">
            Default Variant
          </span>
        }

        {productConfig.has_variant && !variant?.is_default_variant && 
          <button 
            onClick={(e) => markAsDefault(e, variant?.id || false, serial)} 
            className="ml-auto px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors duration-200"
          >
            Make Default
          </button>
        }
      </div>

      {isVariantMissingError && 
        <div className="mb-4 p-3 bg-red-100 border-l-4 border-red-500 text-red-700 rounded">
          <p className="text-sm font-medium">* Product Information Required</p>
        </div>
      }

      {/* Variant Image */}
      <div className="mb-6">
        <ImageField 
          value={variant?.image_details}  
          label="Variant Images" 
          name="image" 
          onChange={(name: string, data:any) => handleSingleChange(name, data[0]?.id)} 
        />
      </div>
      
      {/* Variant Name */}
      {productConfig.has_variant && (
        <div className="mb-6 bg-slate-50 p-4 rounded-xl">
          <div className="w-full">
            <label htmlFor="name" className="font-nunito font-bold text-gray-700 block mb-2">Variant Name</label>
            <InputField
              onChange={onChangeHandler}
              id="name"
              name="name"
              type="text"
              placeholder="Variant Name"
              defaultValue={variant?.name}
              className="w-full px-5 py-3 bg-white mt-1 rounded-xl font-nunito outline-[#0CAF60] placeholder:text-gray-400 border-[2px] border-dashed focus:border-solid transition-colors duration-200"
            />
          </div>
        </div>
      )}

      {/* Price Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        <div className="bg-slate-50 p-4 rounded-xl">
          <label htmlFor="price" className="font-nunito font-bold text-gray-700 block mb-2">Price</label>
          <CurrencyInputField
            errorData={errorData?.variants_payload ? errorData?.variants_payload[serial - 1] : {}}
            onChange={onChangeHandler}
            name="price"
            id="price"
            type="text"
            defaultValue={variant?.price}
            placeholder="$0.00"
            className="w-full px-5 py-3 bg-white mt-1 rounded-xl font-nunito outline-[#0CAF60] placeholder:text-gray-400 border-[2px] border-dashed focus:border-solid transition-colors duration-200"
          />
        </div>
        <div className="bg-slate-50 p-4 rounded-xl">
          <label htmlFor="cost_per_item" className="font-nunito font-bold text-gray-700 block mb-2">Cost per item</label>
          <CurrencyInputField
            errorData={errorData?.variants_payload ? errorData?.variants_payload[serial - 1] : {}}
            onChange={onChangeHandler}
            id="cost_per_item"
            name="cost_per_unit"
            type="text"
            defaultValue={variant?.cost_per_unit}
            placeholder="$0.00"
            className="w-full px-5 py-3 bg-white mt-1 rounded-xl font-nunito outline-[#0CAF60] placeholder:text-gray-400 border-[2px] border-dashed focus:border-solid transition-colors duration-200"
          />
        </div>
        <div className="bg-slate-50 p-4 rounded-xl">
          <label htmlFor="profit" className="font-nunito font-bold text-gray-700 block mb-2">Margin</label>
          <CurrencyInputField
            disabled
            id="profit"
            name="profit"
            type="text"
            value={variant?.price - variant?.cost_per_unit || ''}
            placeholder="--"
            className="w-full px-5 py-3 bg-white mt-1 rounded-xl font-nunito outline-[#0CAF60] placeholder:text-gray-400 border-[2px] border-dashed focus:border-solid transition-colors duration-200"
          />
        </div>
      </div>

      {/* Weight Section */}
      {productConfig.physical_product && (
        <div className="mb-6 bg-slate-50 p-4 rounded-xl">
          <label htmlFor="profit" className="font-nunito font-bold text-gray-700 block mb-2">
            {getEnumItem(enumChoice.weightUnits, productConfig?.weight_unit)?.label}
          </label>
          <InputField
            onChange={onWeightValueChange}
            id="Value"
            name="weight_value"
            type="number"
            placeholder="--"
            defaultValue={variant?.weight_value}
            className="w-full px-5 py-3 bg-white mt-1 rounded-xl font-nunito outline-[#0CAF60] placeholder:text-gray-400 border-[2px] border-dashed focus:border-solid transition-colors duration-200"
          />
        </div>
      )}

      {/* Metadata Section */}
      {metaSection > 0 && (
        <div className="mt-6 bg-slate-50 p-4 rounded-xl">
          <h3 className="font-nunito font-[900] text-gray-800 mb-4">Metadata</h3>
          
          <div className="flex items-center gap-5 mb-3 border-b pb-2">
            <div className="w-full font-nunito font-bold text-gray-700">Name</div>
            <div className="w-full font-nunito font-bold text-gray-700">Value</div>
          </div>
          
          {Array.from({ length: metaSection }, (_, index) => (
            <div key={index} className="flex items-center gap-5 mb-3">
              <div className="w-full">
                <InputField
                  onChange={onChangeHandler}
                  type="text"
                  placeholder="eg. Capacity"
                  className="w-full px-5 py-3 bg-white rounded-xl font-nunito outline-[#0CAF60] placeholder:text-gray-400 border-[2px] border-dashed focus:border-solid transition-colors duration-200"
                />
              </div>
              <div className="w-full">
                <InputField
                  onChange={onChangeHandler}
                  type="text"
                  placeholder="--"
                  className="w-full px-5 py-3 bg-white rounded-xl font-nunito outline-[#0CAF60] placeholder:text-gray-400 border-[2px] border-dashed focus:border-solid transition-colors duration-200"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Inventory Section */}
      {productConfig.track_stock && (
        <div className="mt-6 bg-slate-50 p-4 rounded-xl">
          <h3 className="font-nunito font-[900] text-gray-800 mb-4">Inventory</h3>
          
          <div className="mb-4">
            <label htmlFor="sku-price" className="font-nunito font-bold text-gray-700 block mb-2">SKU</label>
            <InputField
              errorData={errorData?.variants_payload ? errorData?.variants_payload[serial - 1] : {}}
              onChange={onChangeHandler}
              id="sku-price"
              name="sku"
              type="text"
              defaultValue={variant?.sku}
              placeholder="--"
              className="w-full px-5 py-3 bg-white mt-1 rounded-xl font-nunito outline-[#0CAF60] placeholder:text-gray-400 border-[2px] border-dashed focus:border-solid transition-colors duration-200"
            />
          </div>

          <div className="flex items-center gap-3 py-2">
            <InputField
              type="checkbox"
              id="allow_backorder"
              name="allow_backorder"
              checked={variant?.allow_backorder || false}
              onChange={(e) => onSingleChange('allow_backorder', e.target.checked)}
              className="h-5 w-5 text-primary-600 border-gray-300 rounded"
            />
            <label htmlFor="allow_backorder" className="font-nunito font-medium text-gray-700">
              Allow customers to purchase even when out of stock
            </label>
          </div>
        </div>
      )}

      {/* Add Metadata Button */}
      <button 
        onClick={addNewMetasection} 
        className="mt-6 font-nunito font-[800] flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors duration-200"
      >
        <NXPlus className="w-4 h-4" /> Add Metadata
      </button>
    </div>
  );
};

export default VariantSection;